// Service Steady PWA — app-icon variant persistence + DOM swap.
// v1.0 — 2026-05-04 — Premium polish #5.
//
// Lets the owner pick from a small set of installable app icons. The choice
// is stored in localStorage and applied by mutating the apple-touch-icon
// <link> on first paint. iOS standalone PWAs CACHE the install-time icon —
// changing the icon after install requires deleting + reinstalling the
// home-screen shortcut. The Settings UI surfaces this caveat next to the picker.
//
// Real PNGs aren't shipped in this commit — placeholder filenames live in
// /public/icons/variants/ so Ernie can drop in matching artwork without
// touching code. The Settings UI renders solid-color thumbnails as a
// fallback when the PNG is missing (404s gracefully via onError).

const STORAGE_KEY = 'ss_app_icon_v1';

export interface AppIconVariant {
  /** Stable id stored in localStorage. */
  id: string;
  /** Display label in the picker. */
  label: string;
  /** Public path to the apple-touch-icon PNG. */
  appleTouchIcon: string;
  /** Public path to the 192px icon PNG. */
  icon192: string;
  /** Public path to the 512px icon PNG. */
  icon512: string;
  /** Solid color used by the picker thumbnail fallback. */
  swatch: string;
  /** Optional short description shown under the label. */
  hint?: string;
}

export const APP_ICON_VARIANTS: AppIconVariant[] = [
  {
    id: 'default',
    label: 'Default',
    appleTouchIcon: '/icons/apple-touch-icon.png',
    icon192: '/icons/icon-192.png',
    icon512: '/icons/icon-512.png',
    swatch: '#2563EB',
    hint: 'SS blue logo',
  },
  {
    id: 'dark',
    label: 'Dark',
    appleTouchIcon: '/icons/variants/apple-touch-icon-dark.png',
    icon192: '/icons/variants/icon-192-dark.png',
    icon512: '/icons/variants/icon-512-dark.png',
    swatch: '#0F172A',
    hint: 'Inverted for dark home screens',
  },
  {
    id: 'mono',
    label: 'Monogram',
    appleTouchIcon: '/icons/variants/apple-touch-icon-mono.png',
    icon192: '/icons/variants/icon-192-mono.png',
    icon512: '/icons/variants/icon-512-mono.png',
    swatch: '#FFFFFF',
    hint: 'White SS mark on transparent',
  },
  {
    id: 'accent',
    label: 'Accent',
    appleTouchIcon: '/icons/variants/apple-touch-icon-accent.png',
    icon192: '/icons/variants/icon-192-accent.png',
    icon512: '/icons/variants/icon-512-accent.png',
    swatch: '#10B981',
    hint: 'Pops on busy wallpapers',
  },
];

export function readStoredAppIcon(): string {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && APP_ICON_VARIANTS.some((v) => v.id === raw)) return raw;
  } catch {
    /* ignore */
  }
  return 'default';
}

export function persistAppIcon(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* private mode */
  }
}

export function findVariant(id: string): AppIconVariant {
  return APP_ICON_VARIANTS.find((v) => v.id === id) ?? APP_ICON_VARIANTS[0]!;
}

/**
 * Swap the apple-touch-icon + favicon links to point at the chosen variant.
 * iOS only re-reads these on install / "Add to Home Screen" — for already-
 * installed PWAs the home-screen icon stays cached until the user removes +
 * re-adds. Browser-tab favicon updates immediately.
 */
export function applyAppIcon(id: string): void {
  if (typeof document === 'undefined') return;
  const variant = findVariant(id);

  const setLink = (selector: string, href: string) => {
    const link = document.head.querySelector<HTMLLinkElement>(selector);
    if (link) {
      link.href = href;
    }
  };

  setLink('link[rel="apple-touch-icon"]', variant.appleTouchIcon);
  // Favicon — match the 192px to 192/32. The 32 is a smaller crop of the same
  // artwork; we point both at the variant's 192 since favicons scale.
  setLink('link[rel="icon"][sizes="192x192"]', variant.icon192);
  setLink('link[rel="icon"][sizes="32x32"]', variant.icon192);
}

/**
 * Boot helper — wired from main.tsx so the icon swap happens before the
 * browser kicks off favicon fetching for the visible tab.
 */
export function bootAppIcon(): void {
  applyAppIcon(readStoredAppIcon());
}
